import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../utils/api';

// Month abbreviations are operator chrome, so this holds KEYS and t() is called at each use site
// (t() cannot be called at module scope). Order and length are unchanged: index + 1 is still the
// month number everywhere this array is read.
const MONTHS = ['pa_bk_mon_jan', 'pa_bk_mon_feb', 'pa_bk_mon_mar', 'pa_bk_mon_apr', 'pa_bk_mon_may', 'pa_bk_mon_jun', 'pa_bk_mon_jul', 'pa_bk_mon_aug', 'pa_bk_mon_sep', 'pa_bk_mon_oct', 'pa_bk_mon_nov', 'pa_bk_mon_dec'];
const money = (n: any) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const num = (v: any) => Number(v) || 0;

type Client = { id: string; company_name: string; client_name: string; tax_id: string; client_no?: number; is_individual?: number; doc_count?: number; last_year?: number };
type Item = { id: string; description: string; qty: number; unit_price: number; amount: number };
type Line = { id: string; month: number; section: 'output' | 'input'; line_date: string; doc_no: string; party_name: string; amount: number; amount0?: number; total: number; items: Item[]; images?: { id: string; mime: string }[] };

const cell: React.CSSProperties = { fontSize: 12.5, padding: '4px 6px', borderRight: '1px solid var(--border)', verticalAlign: 'middle' };
const inp: React.CSSProperties = { width: '100%', border: '1px solid transparent', background: 'transparent', fontSize: 12.5, padding: '4px 5px', borderRadius: 5, outline: 'none', boxSizing: 'border-box', color: 'var(--text-primary)' };
// 10.5 → 11.5px (Calem, report 66c9d174, 2nd opening: "Do the same for all other menus in the SLF system"). Then Active
// Jobs' headings, as every staff list's (report 4bd4ae4e, 2nd opening: "Make all menus in the SLF system look like this:
// the same height, text color, no bold, horizontal line colors etc."): black, --text-primary, at the regular weight, where
// they were dark grey in bold, 10px above and below where it was 6, on one 16px line, a 37px band, on white. The lines
// down between the columns stay.
const th: React.CSSProperties = { fontSize: 11.5, fontWeight: 400, color: 'var(--text-primary)', padding: '10px 6px', lineHeight: '16px', borderRight: '1px solid var(--border)', borderBottom: '1px solid var(--border-strong)', textAlign: 'left', whiteSpace: 'nowrap', background: 'var(--bg-surface)' };
const ddl: React.CSSProperties = { padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', cursor: 'pointer' };
const lbBtn: React.CSSProperties = { border: '1px solid #ffffff55', background: '#ffffff22', color: '#fff', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 14 };

// Who this list is for (owner 2026-09-25). Bookkeeping is the companies' invoice-item work, so the
// list shows COMPANIES THAT HAVE INVOICES — 129 of the firm's 267 accounting clients. Individuals
// and clients with no document yet are held back, because they were most of what filled the screen:
// the old query ordered by company_name, 8 accounting clients have an empty one, and empty sorts
// first, so this page opened on eight nameless people every time.
//
// Held back, not lost: "Show every client" drops both filters, which is how a client who signed up
// last week is opened for the first time, and how the 28 individuals who do carry invoice items are
// still reached. The line under the box always says how many are being held back, so a short list is
// never a mystery. Each row now carries the client's number — the one staff say out loud and the one
// the AI Working Reports board prints — plus how many invoices are on file and the latest year.
function ClientPicker({ onPick }: { onPick: (c: Client) => void }) {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [all, setAll] = useState(false);
  const [rows, setRows] = useState<Client[]>([]);
  const [info, setInfo] = useState<{ total: number; pool: number; filtered: boolean }>({ total: 0, pool: 0, filtered: true });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const h = setTimeout(() => {
      setLoading(true);
      api.bkSearchClients(q, 1, all)
        .then((d: any) => { setRows(d.rows || []); setInfo({ total: Number(d.total) || 0, pool: Number(d.pool) || 0, filtered: !!d.filtered }); })
        .catch(() => { setRows([]); })
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(h);
  }, [q, all]);
  const more = Math.max(0, info.total - rows.length);      // 100 at a time — say what is over the edge
  const hidden = info.filtered && !q ? Math.max(0, info.pool - info.total) : 0;
  return (
    <div style={{ maxWidth: 980, margin: '0 auto', padding: '24px 16px', width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{t('bk_pick_client')}</div>
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{info.total}</div>
        <div style={{ flex: 1 }} />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--text-secondary)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
          <input type="checkbox" checked={all} onChange={e => setAll(e.target.checked)} style={{ cursor: 'pointer' }} />
          {t('bk_show_all')}
        </label>
      </div>
      <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder={t('bk_search_client')}
        style={{ width: '100%', padding: '11px 12px', fontSize: 14, borderRadius: 10, border: '1px solid var(--border-strong)', background: 'var(--c-gray-50)', outline: 'none', boxSizing: 'border-box' }} />
      <div style={{ minHeight: 18, margin: '6px 2px 10px', fontSize: 11.5, color: 'var(--text-tertiary)' }}>
        {hidden > 0 ? t('bk_hidden_n', { n: hidden }) : more > 0 ? t('tx_more_results', { n: more }) : ''}
      </div>
      <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
        {loading ? <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" /></div>
          : rows.length === 0 ? <div style={{ padding: 28, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>{t('tx_no_client')}</div>
          : rows.map(c => (
            <div key={c.id} onClick={() => onPick(c)} style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12 }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-active)')} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
              {/* The client's own number — fixed width so the names below it still line up. */}
              <span style={{ width: 46, flexShrink: 0, fontSize: 11.5, fontWeight: 700, color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums' }}>
                {c.client_no ? `#${String(c.client_no).padStart(3, '0')}` : '—'}
              </span>
              <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {c.company_name || c.client_name || '—'}
                {!!c.is_individual && <i className="ti ti-user" title={t('pa_bk_individual')} style={{ marginLeft: 7, fontSize: 13, color: 'var(--text-tertiary)' }} aria-hidden />}
              </span>
              <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                {c.doc_count ? `${c.doc_count === 1 ? t('bk_doc_1') : t('bk_docs_n', { n: c.doc_count })}${c.last_year ? ` · ${c.last_year}` : ''}` : ''}
              </span>
              <span style={{ width: 120, flexShrink: 0, textAlign: 'right', fontSize: 12, color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums' }}>{c.tax_id || '—'}</span>
            </div>
          ))}
      </div>
    </div>
  );
}

type MonthRow = { month: number; lines: number; sales: number; purchase: number; itemised: number; exceptions: number; filed: boolean };

// ── The year, one tile per month (owner 2026-09-25, phase 1) ─────────────────
// What a client opens on now. The module had a month FILTER but no period OBJECT: a filter answers
// "show me September", it cannot answer "which months am I behind on" — and that is the only
// question asked before opening anything, because Thai VAT is filed monthly, so the month is the
// unit of the work rather than a view of it. The tile is the Tax module's month card with
// bookkeeping's numbers in its slots, deliberately: staff already read that grid every month, and a
// second idiom for the same idea would be a new thing to learn for no gain.
//
// It also ends the flat-year render. "All months" used to fetch every invoice of the year and mount
// a card each — 1,783 of them at the heaviest client — and now costs three grouped queries and no
// line data at all.
function YearGrid({ months, year, onPick }: { months: MonthRow[]; year: number; onPick: (m: number) => void }) {
  const { t } = useTranslation();
  const now = new Date();
  const thisMonth = now.getFullYear() === year ? now.getMonth() + 1 : 0;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(196px, 1fr))', gap: 10 }}>
      {months.map(m => {
        const done = m.lines > 0 && m.itemised >= m.lines;
        const empty = m.lines === 0;
        return (
          <button key={m.month} onClick={() => onPick(m.month)}
            style={{ textAlign: 'left', border: `1px solid ${m.month === thisMonth ? 'var(--accent)' : 'var(--border)'}`, borderRadius: 10, padding: '12px 14px', background: 'var(--bg-surface)', cursor: 'pointer', opacity: empty ? 0.62 : 1 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
              <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-primary)' }}>{t(MONTHS[m.month - 1])} {year}</span>
              <div style={{ flex: 1 }} />
              {done && <span style={{ fontSize: 10.5, fontWeight: 800, color: '#15803D' }}>✓</span>}
              {m.filed && <span title={t('bk_m_filed')} style={{ fontSize: 10.5, fontWeight: 800, color: 'var(--text-tertiary)' }}><i className="ti ti-lock" aria-hidden /></span>}
            </div>
            <div style={{ fontSize: 19, fontWeight: 800, color: empty ? 'var(--text-tertiary)' : 'var(--accent)', margin: '2px 0 1px' }}>{empty ? '—' : m.lines}</div>
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
              {empty ? t('bk_m_empty') : `${t('bk_m_sales')} ${m.sales} · ${t('bk_m_purchase')} ${m.purchase}`}
            </div>
            {!empty && (
              <div style={{ fontSize: 11, marginTop: 2, color: done ? '#15803D' : 'var(--text-tertiary)' }}>
                {t('bk_m_itemised', { done: m.itemised, total: m.lines })}
              </div>
            )}
            {m.exceptions > 0 && (
              <div style={{ fontSize: 11, marginTop: 2, color: '#B45309', fontWeight: 700 }}>{t('bk_m_check', { n: m.exceptions })}</div>
            )}
          </button>
        );
      })}
    </div>
  );
}

type BookRow = {
  id: string; line_date: string; doc_no: string; party_name: string; party_tax_id: string;
  branch_head: number; branch_no: string; amount: number; amount0: number; vat: number; total: number;
  filing_no: number; calc_error: string; item_n: number; item_sum: number; img_n: number; recon_ok: number;
};
type BookFlags = { all_n: number; noitems: number; mismatch: number; nophoto: number; quarantined: number };
type Sub = { filing_no: number; n: number; base: number; base0: number; vat: number; gross: number };
type Book = { rows: BookRow[]; totals: { n: number; base: number; base0: number; vat: number; gross: number }; flags: BookFlags; subs: Sub[]; filed: boolean; filedCeiling: number };

// Whether an invoice reconciles is decided in SQL now (RECON_OK in routes/bookkeeping.ts) and
// arrives as recon_ok, so the rows, the totals and the counts on the chips can never disagree about
// what an exception is. This mirror exists for ONE job: keeping a row's badge honest between a
// keystroke in the editor below it and the next time the book is fetched. The server is the
// authority; if the two ever have to differ, change the SQL and let this follow.
const reconciles = (item_sum: number, amount: number, amount0: number, total: number, item_n: number) =>
  item_n === 0 || Math.abs(item_sum - total) < 1 || Math.abs(item_sum - amount) < 1
  || Math.abs(item_sum - (amount + (amount0 || 0))) < 1;

// ── The item editor, opened under one invoice row ────────────────────────────
// Lifted from the card this screen used to be made of: the same grid, the same add / delete /
// clear-all / regenerate, the same photo viewer. What changed is WHEN it exists — one row at a time,
// instead of every invoice of the month mounting its own table and its ~6 inputs per item.
function ItemsEditor({ line, clientId, onImage, onEditing, onCounts }: {
  line: BookRow; clientId: string;
  onImage: (imgs: any[]) => void; onEditing: (v: boolean) => void;
  onCounts: (lineId: string, item_n: number, item_sum: number) => void;
}) {
  const { t } = useTranslation();
  const [items, setItems] = useState<Item[] | null>(null);
  const [images, setImages] = useState<{ id: string; mime: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const load = async () => {
    try { const d: any = await api.bkLineItems(line.id); setItems(d.items || []); setImages(d.images || []); }
    catch (e: any) { setItems([]); setErr(e?.message || t('pa_bk_err_items_load')); }
  };
  useEffect(() => { load(); }, [line.id]);   // eslint-disable-line
  const report = (next: Item[]) => onCounts(line.id, next.length, next.reduce((a, it) => a + num(it.amount), 0));

  const change = (id: string, patch: Partial<Item>) => setItems(p => { const n = (p || []).map(it => it.id === id ? { ...it, ...patch } : it); report(n); return n; });
  const save = async (id: string) => {
    const it = (items || []).find(x => x.id === id); if (!it) return;
    try { setErr(''); await api.bkUpdateItem(id, { description: it.description, qty: num(it.qty), unit_price: num(it.unit_price), amount: num(it.amount) }); }
    catch (e: any) { setErr(e?.message || t('pa_bk_err_not_saved')); }        // a refused save no longer passes for a saved one
  };
  const add = async () => {
    try { setErr(''); const r: any = await api.bkAddItem(line.id, { description: '', qty: 1, unit_price: 0, amount: 0 }); setItems(p => { const n = [...(p || []), { id: r.id, description: '', qty: 1, unit_price: 0, amount: 0 }]; report(n); return n; }); }
    catch (e: any) { setErr(e?.message || t('pa_bk_err_add')); }
  };
  const del = async (id: string) => {
    const prev = items || [];
    setItems(p => { const n = (p || []).filter(it => it.id !== id); report(n); return n; });
    try { setErr(''); await api.bkDeleteItem(id); }
    catch (e: any) { setItems(prev); report(prev); setErr(e?.message || t('pa_bk_err_delete')); }
  };
  const clearAll = async () => {
    if (!window.confirm(t('bk_clear_all_confirm'))) return;
    const prev = items || [];
    setItems([]); report([]);
    try { setErr(''); await api.bkDeleteAllItems(line.id); }
    catch (e: any) { setItems(prev); report(prev); setErr(e?.message || t('pa_bk_err_clear')); }
  };
  // Refused on a filed month, with the Tax module's own wording — see routes/bookkeeping.ts.
  const regenerate = async () => {
    if (busy || !window.confirm(t('bk_regen_confirm'))) return;
    setBusy(true); setErr(''); onEditing(true);
    try {
      const r: any = await api.bkRegenerate(clientId, line.id);
      await load();
      const probs: string[] = [];
      if (r.dateCandidates && r.dateCandidates.length) probs.push(t('bk_regen_date', { dates: r.dateCandidates.join(' / ') }));
      if (r.missing && r.missing.length) probs.push(t('bk_regen_missing'));
      if (probs.length) setErr(probs.join(' · '));
    } catch (e: any) { setErr(e?.message || t('pa_bk_err_regen')); }
    finally { setBusy(false); onEditing(false); }
  };

  const sum = (items || []).reduce((a, it) => a + num(it.amount), 0);
  const ok = !items || items.length === 0 || Math.abs(sum - num(line.total)) < 1 || Math.abs(sum - num(line.amount)) < 1 || Math.abs(sum - (num(line.amount) + num(line.amount0))) < 1;

  return (
    <div style={{ position: 'relative', padding: '10px 12px 12px', background: 'var(--c-gray-50)', borderTop: '1px solid var(--border)' }}>
      {busy && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 5, background: 'rgba(255,255,255,0.82)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <i className="ti ti-loader-2" style={{ fontSize: 24, color: 'var(--accent)', display: 'inline-block', animation: 'spin 0.8s linear infinite' }} />
          <div style={{ fontSize: 13, fontWeight: 700 }}>{t('bk_regen_loading')}</div>
          <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{t('bk_regen_loading_hint')}</div>
        </div>
      )}
      {err && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 11px', marginBottom: 9, borderRadius: 8, background: '#FFFBEB', border: '1px solid #FDE68A', color: '#92400E', fontSize: 12 }}>
          <i className="ti ti-alert-triangle" aria-hidden /><span style={{ flex: 1 }}>{err}</span>
          <button onClick={() => setErr('')} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#92400E' }}><i className="ti ti-x" /></button>
        </div>
      )}
      {items === null ? <div style={{ padding: 14, color: 'var(--text-tertiary)', fontSize: 12.5 }}><i className="ti ti-loader" /></div> : (<>
        <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg-surface)' }}>
          <table data-nozebra style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
            <thead><tr>
              <th style={{ ...th, width: 28 }}>#</th><th style={th}>{t('bk_desc')}</th>
              <th style={{ ...th, width: 70, textAlign: 'right' }}>{t('bk_qty')}</th>
              <th style={{ ...th, width: 100, textAlign: 'right' }}>{t('bk_unit_price')}</th>
              <th style={{ ...th, width: 110, textAlign: 'right' }}>{t('bk_items')}</th>
              <th style={{ ...th, width: 28, borderRight: 'none' }} /></tr></thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={it.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ ...cell, textAlign: 'center', color: 'var(--text-tertiary)' }}>{i + 1}</td>
                  <td style={cell}><input value={it.description} onFocusCapture={() => onEditing(true)} onChange={e => change(it.id, { description: e.target.value })} onBlur={() => { onEditing(false); save(it.id); }} style={inp} /></td>
                  <td style={cell}><input value={it.qty} onFocusCapture={() => onEditing(true)} onChange={e => change(it.id, { qty: e.target.value as any })} onBlur={() => { onEditing(false); save(it.id); }} style={{ ...inp, textAlign: 'right' }} inputMode="decimal" /></td>
                  <td style={cell}><input value={it.unit_price} onFocusCapture={() => onEditing(true)} onChange={e => change(it.id, { unit_price: e.target.value as any })} onBlur={() => { onEditing(false); save(it.id); }} style={{ ...inp, textAlign: 'right' }} inputMode="decimal" /></td>
                  <td style={cell}><input value={it.amount} onFocusCapture={() => onEditing(true)} onChange={e => change(it.id, { amount: e.target.value as any })} onBlur={() => { onEditing(false); save(it.id); }} style={{ ...inp, textAlign: 'right' }} inputMode="decimal" /></td>
                  <td style={{ ...cell, borderRight: 'none', textAlign: 'center' }}>
                    <button onClick={() => del(it.id)} title={t('pa_bk_item_remove')} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-tertiary)' }}><i className="ti ti-x" style={{ fontSize: 13 }} /></button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={6} style={{ ...cell, borderRight: 'none', padding: 14, textAlign: 'center', color: 'var(--text-tertiary)' }}>{t('bk_no_items')}</td></tr>}
              {items.length > 0 && (
                <tr><td colSpan={4} style={{ ...cell, borderRight: 'none', textAlign: 'right', fontWeight: 700, color: 'var(--text-secondary)' }}>{t('bk_items')}</td>
                  <td style={{ ...cell, textAlign: 'right', fontWeight: 800, color: ok ? 'var(--text-primary)' : '#DC2626' }}>{money(sum)}</td>
                  <td style={{ ...cell, borderRight: 'none' }} /></tr>
              )}
            </tbody>
          </table>
        </div>
        {!ok && <div style={{ marginTop: 8, padding: '7px 11px', borderRadius: 8, background: '#FFFBEB', border: '1px solid #FDE68A', color: '#B45309', fontSize: 12 }}>{t('bk_recon_mismatch', { sum: money(sum), total: money(line.total) })}</div>}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 9, flexWrap: 'wrap' }}>
          <button onClick={add} style={{ ...ddl, padding: '5px 11px', fontSize: 12.5 }}><i className="ti ti-plus" style={{ fontSize: 13 }} /> {t('bk_add_item')}</button>
          {items.length > 0 && <button onClick={clearAll} style={{ ...ddl, padding: '5px 11px', fontSize: 12.5, color: '#B42318', borderColor: 'rgba(180,35,24,0.35)' }}><i className="ti ti-trash" style={{ fontSize: 13 }} /> {t('bk_clear_all')}</button>}
          <div style={{ flex: 1 }} />
          {images.length > 0 && <button onClick={() => onImage(images)} style={{ ...ddl, padding: '5px 11px', fontSize: 12.5 }}><i className="ti ti-photo" style={{ fontSize: 13 }} /> {t('bk_view_photo')}{images.length > 1 ? ` (${images.length})` : ''}</button>}
          {images.length > 0 && <button onClick={regenerate} disabled={busy} style={{ ...ddl, padding: '5px 11px', fontSize: 12.5 }}><i className="ti ti-refresh" style={{ fontSize: 13 }} /> {t('bk_regen')}</button>}
        </div>
      </>)}
    </div>
  );
}

// ── One statutory book ───────────────────────────────────────────────────────
// รายงานภาษีขาย (output) or รายงานภาษีซื้อ (input): one row per tax invoice, in the RD form's own
// column order, footed by the totals that go on ภ.พ.30. The numbering restarts at 1 inside each book
// each month, the way the paper books do, so "Purchase #42, September" is an address someone can say
// out loud — it comes from the sort, not from storage, which is why doc_no stays the identity.
//
// Header fields are READ-ONLY here. The Tax module owns the invoice header; this screen owns the
// items underneath it. That division is what keeps two screens over one table honest.
function BookTable({ book, section, clientId, openId, onOpen, onImage, onEditing, onCounts }: {
  book: Book | null; section: 'output' | 'input'; clientId: string;
  openId: string; onOpen: (id: string) => void;
  onImage: (imgs: any[]) => void; onEditing: (v: boolean) => void;
  onCounts: (lineId: string, item_n: number, item_sum: number) => void;
}) {
  const { t } = useTranslation();
  const accent = section === 'output' ? '#2B6CB0' : '#1D9E75';
  const rows = book?.rows || [];
  const has0 = rows.some(r => Number(r.amount0) > 0);
  const tot = book?.totals;
  // A month may hold several submissions — the Normal return and later Additional ones. Each is a
  // separate return, so each is its own block with its own footing; with only one, nothing is drawn
  // and the book reads exactly as it did. A block whose submission has been filed says so.
  const subs = book?.subs || [];
  const grouped = subs.length > 1;
  const subOf = (no: number) => subs.find(x => x.filing_no === no);
  const cols = has0 ? 10 : 9;
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 9, marginBottom: 7, flexWrap: 'wrap' }}>
        <span style={{ width: 9, height: 9, borderRadius: 2, background: accent, display: 'inline-block' }} aria-hidden />
        <span style={{ fontSize: 14, fontWeight: 800, color: 'var(--text-primary)' }}>{t(section === 'output' ? 'tx_output' : 'tx_input')}</span>
        {tot && (
          <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums' }}>
            {tot.n} · {t('tx_amount')} {money(tot.base)}{has0 ? ` · ${t('pa_bk_zero_rate')} ${money(tot.base0)}` : ''} · {t('tx_tax')} {money(tot.vat)} · {t('tx_total')} {money(tot.gross)} <span style={{ opacity: 0.75 }}>({t('bk_as_listed')})</span>
          </span>
        )}
      </div>
      {!book ? <div style={{ padding: 18, color: 'var(--text-tertiary)' }}><i className="ti ti-loader" /></div>
        : rows.length === 0 ? <div style={{ padding: 18, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 12.5, border: '1px solid var(--border)', borderRadius: 8 }}>{t('bk_no_invoices')}</div> : (
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', background: 'var(--bg-surface)' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
              <thead><tr>
                <th style={{ ...th, width: 34, textAlign: 'right' }} title={t('bk_no_hint')}>#</th>
                <th style={{ ...th, width: 96 }}>{t('tx_date')}</th>
                <th style={{ ...th, width: 130 }}>{t('tx_doc')}</th>
                <th style={th}>{t('tx_party')}</th>
                <th style={{ ...th, width: 120 }}>{t('tx_party_tax')}</th>
                <th style={{ ...th, width: 110, textAlign: 'right' }}>{t('tx_amount')}</th>
                {has0 && <th style={{ ...th, width: 100, textAlign: 'right' }}>{t('pa_bk_zero_rate')}</th>}
                <th style={{ ...th, width: 96, textAlign: 'right' }}>{t('tx_tax')}</th>
                <th style={{ ...th, width: 110, textAlign: 'right' }}>{t('tx_total')}</th>
                <th style={{ ...th, width: 92, borderRight: 'none' }}>{t('bk_items')}</th>
              </tr></thead>
              <tbody>
                {rows.map((r, i) => {
                  const open = openId === r.id;
                  const bad = !r.recon_ok;
                  const none = r.item_n === 0;
                  const head = grouped && (i === 0 || rows[i - 1].filing_no !== r.filing_no);
                  const sub = head ? subOf(r.filing_no) : null;
                  const subFiled = !!book && r.filing_no <= (book.filedCeiling ?? -1);
                  return (
                    <React.Fragment key={r.id}>
                      {head && (
                        <tr data-nozebra><td colSpan={cols} style={{ padding: '7px 8px', background: 'var(--c-gray-50)', borderBottom: '1px solid var(--border)', borderTop: i ? '1px solid var(--border-strong)' : undefined }}>
                          <span style={{ fontSize: 11.5, fontWeight: 800, color: 'var(--text-secondary)', letterSpacing: 0.2 }}>
                            {r.filing_no === 0 ? t('tx_fl_normal') : `${t('tx_fl_add')} ${r.filing_no}`}
                          </span>
                          {subFiled && <i className="ti ti-lock" title={t('tx_fl_locked_tip')} style={{ marginLeft: 6, fontSize: 12, color: 'var(--text-tertiary)' }} aria-hidden />}
                          {sub && (
                            <span style={{ marginLeft: 10, fontSize: 11, color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums' }}>
                              {sub.n} · {t('tx_amount')} {money(sub.base)}{has0 ? ` · ${t('pa_bk_zero_rate')} ${money(sub.base0)}` : ''} · {t('tx_tax')} {money(sub.vat)} · {t('tx_total')} {money(sub.gross)}
                            </span>
                          )}
                        </td></tr>
                      )}
                      <tr onClick={() => onOpen(open ? '' : r.id)} style={{ cursor: 'pointer', borderBottom: '1px solid var(--border)', background: open ? 'var(--bg-active)' : undefined }}>
                        <td style={{ ...cell, textAlign: 'right', color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums' }}>{i + 1}</td>
                        <td style={{ ...cell, whiteSpace: 'nowrap' }}>{r.line_date || '—'}</td>
                        <td style={{ ...cell, whiteSpace: 'nowrap', fontWeight: 600 }}>{r.doc_no || '—'}</td>
                        <td style={cell}>
                          <i className={`ti ti-chevron-${open ? 'down' : 'right'}`} style={{ fontSize: 12, color: 'var(--text-tertiary)', marginRight: 6 }} aria-hidden />
                          {r.party_name || '—'}
                          {!!r.calc_error && <span title={r.calc_error} style={{ marginLeft: 7, fontSize: 10, fontWeight: 800, color: '#B42318', background: '#FCEBEB', padding: '1px 6px', borderRadius: 4 }}>!</span>}
                          {r.filing_no > 0 && <span style={{ marginLeft: 7, fontSize: 10, fontWeight: 800, color: '#92400E', background: '#FFFBEB', padding: '1px 6px', borderRadius: 4 }}>A{r.filing_no}</span>}
                        </td>
                        <td style={{ ...cell, whiteSpace: 'nowrap', color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums' }}>{r.party_tax_id || '—'}</td>
                        <td style={{ ...cell, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{money(r.amount)}</td>
                        {has0 && <td style={{ ...cell, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--text-tertiary)' }}>{Number(r.amount0) ? money(r.amount0) : '—'}</td>}
                        <td style={{ ...cell, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{money(r.vat)}</td>
                        <td style={{ ...cell, textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{money(r.total)}</td>
                        <td style={{ ...cell, borderRight: 'none', whiteSpace: 'nowrap' }}>
                          <span style={{ fontSize: 10.5, fontWeight: 800, padding: '2px 7px', borderRadius: 999, background: bad ? '#FCEBEB' : none ? '#FFFBEB' : '#ECFDF5', color: bad ? '#B42318' : none ? '#92400E' : '#15803D' }}>
                            {bad ? '≠' : none ? t('bk_no_items_short') : r.item_n}
                          </span>
                          {r.img_n > 0 && <i className="ti ti-photo" style={{ marginLeft: 6, fontSize: 13, color: 'var(--text-tertiary)' }} aria-hidden />}
                        </td>
                      </tr>
                      {open && (
                        <tr data-nozebra><td colSpan={cols} style={{ padding: 0, borderBottom: '1px solid var(--border)' }}>
                          <ItemsEditor line={r} clientId={clientId} onImage={onImage} onEditing={onEditing} onCounts={onCounts} />
                        </td></tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          {tot && tot.n > rows.length && (
            <div style={{ padding: '8px 12px', fontSize: 11.5, color: 'var(--text-tertiary)', borderTop: '1px solid var(--border)' }}>{t('bk_showing_n', { shown: rows.length, total: tot.n })}</div>
          )}
        </div>
      )}
    </div>
  );
}


// Full-screen viewer; navigates every page of a multi-sheet invoice (‹ › + counter).
function Lightbox({ images, onClose }: { images: { id: string; mime: string }[]; onClose: () => void }) {
  const { t } = useTranslation();
  const [i, setI] = useState(0);
  const [src, setSrc] = useState('');
  const [imgErr, setImgErr] = useState('');
  const cur = images[i];
  const isPdf = !!cur && cur.mime === 'application/pdf';
  // bkImageBlob, not taxImageBlob: the photo now comes through this module's own permission. And a
  // failure SAYS so — swallowing it left the overlay on "Loading…" for good, which reads as a broken
  // system rather than as a missing file.
  useEffect(() => {
    let url = ''; let alive = true;
    setSrc(''); setImgErr('');
    if (cur) api.bkImageBlob(cur.id)
      .then((u: string) => { if (alive) { url = u; setSrc(u); } })
      .catch((e: any) => { if (alive) setImgErr(e?.message || t('pa_bk_err_photo')); });
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [cur?.id]); // eslint-disable-line
  useEffect(() => { const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); }; window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h); }, []); // eslint-disable-line
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.82)', zIndex: 2000, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div onClick={e => e.stopPropagation()} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
        {src ? (isPdf ? <iframe title="pdf" src={src} style={{ width: '82vw', height: '84vh', border: 'none', background: '#fff', borderRadius: 6 }} /> : <img src={src} alt="" style={{ maxWidth: '92vw', maxHeight: '82vh', objectFit: 'contain', borderRadius: 6 }} />)
          : imgErr ? <div style={{ color: '#fff', maxWidth: 460, textAlign: 'center', lineHeight: 1.5 }}><i className="ti ti-alert-triangle" style={{ fontSize: 22, display: 'block', marginBottom: 6 }} aria-hidden />{imgErr}</div>
          : <div style={{ color: '#fff' }}>{t('pa_bk_loading')}</div>}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {images.length > 1 && <>
            <button onClick={() => setI(p => (p - 1 + images.length) % images.length)} style={lbBtn}>‹</button>
            <span style={{ color: '#fff', fontSize: 13 }}>{i + 1} / {images.length}</span>
            <button onClick={() => setI(p => (p + 1) % images.length)} style={lbBtn}>›</button>
          </>}
          <button onClick={onClose} style={lbBtn}>✕</button>
        </div>
      </div>
    </div>
  );
}

export default function Bookkeeping({ embedded }: { embedded?: boolean } = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [client, setClient] = useState<Client | null>(null);
  const [year, setYear] = useState(() => Number(params.get('year')) || new Date().getFullYear());
  // month 0 is the year grid (phase 1). A client is opened on THIS month, because Thai VAT is filed
  // monthly and that is the month being worked; an explicit ?month= in a link still wins, including
  // ?month=0 for the year.
  const [month, setMonth] = useState(() => {
    const p = params.get('month');
    return p === null ? new Date().getMonth() + 1 : Number(p) || 0;
  });
  const [books, setBooks] = useState<{ output: Book | null; input: Book | null }>({ output: null, input: null });
  // Which slice of the month is on screen (phase 3). 'all' is the default, so the normal case is
  // never made harder by the exceptions — the chips are there to be CLEARED, and each one says how
  // many are left whether or not it is the one selected.
  const [flag, setFlag] = useState('all');
  const [openId, setOpenId] = useState('');
  // This module is the VAT sub-ledger and the invoice line-item work on top of it — nothing else.
  // The statutory books moved out to SLF™ Accounting (/m/accounting_books) once they grew a
  // chart of accounts, five journals, a closing action and financial statements: that is a
  // different job, done by different people, behind its own permission.
  // Year-wide search (phase 5). The grid says WHERE the work is; this answers "where is THAT
  // invoice" — the one thing the flat all-year list was quietly used for. It searches the document
  // number, the party and the ITEM DESCRIPTIONS, which nothing could search before at all.
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<{ rows: any[]; total: number } | null>(null);
  // A hit has to survive the jump into its month: selecting a month clears the open row, so the id
  // waits here and is applied by that same effect.
  const pendingOpen = useRef('');
  const [refreshing, setRefreshing] = useState(false);
  const [lightbox, setLightbox] = useState<{ id: string; mime: string }[] | null>(null);
  const [err, setErr] = useState('');
  const sigRef = useRef('');
  const editingRef = useRef(false);

  const [ov, setOv] = useState<{ years: number[]; months: MonthRow[] } | null>(null);
  // The overview is loaded for the client + year whatever month is open: three grouped queries, it
  // feeds the year dropdown with the years this client ACTUALLY has, and it is what the back arrow
  // from a month returns to.
  useEffect(() => {
    if (!client) { setOv(null); return; }
    let alive = true;
    api.bkOverview(client.id, year)
      .then((d: any) => { if (alive) setOv({ years: d.years || [], months: d.months || [] }); })
      .catch(() => { if (alive) setOv({ years: [], months: [] }); });
    return () => { alive = false; };
  }, [client, year]);   // eslint-disable-line

  // Two books per month, fetched as two queries with no item rows in them — the items of one
  // invoice load when its row is opened. A failure says so; it used to be swallowed, which made a
  // refused request indistinguishable from an empty month.
  const loadBooks = async (silent = false) => {
    if (!client || !month) { setBooks({ output: null, input: null }); return; }
    if (!silent) setBooks({ output: null, input: null });
    try {
      const [o, i2] = await Promise.all([
        api.bkBook(client.id, year, month, 'output', flag),
        api.bkBook(client.id, year, month, 'input', flag),
      ]);
      setBooks({ output: o as any, input: i2 as any });
      setErr('');
    } catch (e: any) { setErr(e?.message || t('pa_bk_err_month_load')); }
  };
  const refresh = async () => {
    if (!client || refreshing) return;
    setRefreshing(true);
    const start = Date.now();
    try {
      if (month) { await loadBooks(true); const s: any = await api.bkSig(client.id, year, month); sigRef.current = s.sig; }
      const d: any = await api.bkOverview(client.id, year); setOv({ years: d.years || [], months: d.months || [] });
    } catch { /* the banner below already carries a real failure */ }
    setTimeout(() => setRefreshing(false), Math.max(0, 650 - (Date.now() - start)));
  };

  useEffect(() => {
    setOpenId(pendingOpen.current); pendingOpen.current = '';
    if (!client) return;
    loadBooks();
    sigRef.current = '';
    if (month) api.bkSig(client.id, year, month).then((s: any) => { sigRef.current = s.sig; }).catch(() => {});
  }, [client, year, month, flag]);   // eslint-disable-line
  useEffect(() => { setFlag('all'); }, [client, year, month]);   // a new month starts on the whole month

  // Debounced, and only from the year view — inside a month the books are already the answer.
  useEffect(() => {
    if (!client || month) { setHits(null); return; }
    if (q.trim().length < 2) { setHits(null); return; }
    let alive = true;
    const h = setTimeout(() => {
      api.bkSearch(client.id, year, q.trim())
        .then((d: any) => { if (alive) setHits({ rows: d.rows || [], total: Number(d.total) || 0 }); })
        .catch(() => { if (alive) setHits({ rows: [], total: 0 }); });
    }, 260);
    return () => { alive = false; clearTimeout(h); };
  }, [client, year, month, q]);   // eslint-disable-line

  // Straight to the invoice: its month, its book, opened.
  const goToHit = (r: any) => { pendingOpen.current = r.id; setQ(''); setHits(null); selectMonth(Number(r.month) || 1); };

  // Live auto-refresh: poll the signature; when it changes (a new Telegram upload, a colleague) and
  // nothing is being typed, pull the books again quietly.
  useEffect(() => {
    if (!client || !month) return;   // the grid is not a live view
    const h = setInterval(async () => {
      try {
        const s: any = await api.bkSig(client.id, year, month);
        if (sigRef.current && s.sig !== sigRef.current && !editingRef.current) { await loadBooks(true); sigRef.current = s.sig; }
      } catch { /* next tick */ }
    }, 15000);
    return () => clearInterval(h);
  }, [client, year, month, flag]);   // eslint-disable-line

  const pick = (c: Client | null) => { setClient(c); setBooks({ output: null, input: null }); if (c) params.set('client', c.id); else params.delete('client'); setParams(params, { replace: true }); };
  // The period rides in the URL for the same reason the client does: a reload comes back to the
  // month being worked.
  const putParam = (k: string, v: string) => setParams(p => { const n = new URLSearchParams(p); n.set(k, v); return n; }, { replace: true });
  const selectYear = (y: number) => { setYear(y); putParam('year', String(y)); setMonth(0); putParam('month', '0'); };
  const selectMonth = (m: number) => { setMonth(m); putParam('month', String(m)); };
  useEffect(() => { const cid = params.get('client'); if (cid && !client) api.bkGet(cid, year, 1).then((d: any) => setClient(d.client)).catch(() => {}); }, []); // eslint-disable-line

  // A row's badge follows what the open editor just did, without re-fetching the whole book. This
  // is the one place the client decides whether an invoice reconciles, and only until the next
  // fetch replaces it with the server's answer — see the note on `reconciles`.
  const touch = (r: BookRow, item_n: number, item_sum: number): BookRow =>
    ({ ...r, item_n, item_sum, recon_ok: reconciles(item_sum, r.amount, r.amount0, r.total, item_n) ? 1 : 0 });
  const onCounts = (lineId: string, item_n: number, item_sum: number) => setBooks(p => ({
    output: p.output ? { ...p.output, rows: p.output.rows.map(r => r.id === lineId ? touch(r, item_n, item_sum) : r) } : null,
    input: p.input ? { ...p.input, rows: p.input.rows.map(r => r.id === lineId ? touch(r, item_n, item_sum) : r) } : null,
  }));

  // The chips count the WHOLE month — both books — so "3 not reconciling" means three left to clear,
  // not three in whichever book you happen to be looking at.
  const fl = (k: keyof BookFlags) => (books.output?.flags?.[k] || 0) + (books.input?.flags?.[k] || 0);
  const CHIPS: { key: string; label: string; n: number; tone?: string }[] = [
    { key: 'all', label: t('pk_all'), n: fl('all_n') },
    { key: 'noitems', label: t('bk_f_noitems'), n: fl('noitems'), tone: '#92400E' },
    { key: 'mismatch', label: t('bk_f_mismatch'), n: fl('mismatch'), tone: '#B42318' },
    { key: 'nophoto', label: t('bk_f_nophoto'), n: fl('nophoto'), tone: '#92400E' },
    { key: 'quarantined', label: t('bk_f_bad'), n: fl('quarantined'), tone: '#B42318' },
  ];
  const shownRows = (books.output?.rows?.length || 0) + (books.input?.rows?.length || 0);

  const years = Array.from(new Set([...(ov?.years || []), new Date().getFullYear(), year])).sort((a, b) => b - a);
  const yearTotals = (ov?.months || []).reduce((a, m) => ({ lines: a.lines + m.lines, done: a.done + m.itemised, n: a.n + m.exceptions }), { lines: 0, done: 0, n: 0 });
  const filed = books.output?.filed || books.input?.filed;

  return (
    <div style={{ ...(embedded ? { flex: 1, minHeight: 0 } : { height: '100vh' }), display: 'flex', flexDirection: 'column', background: 'var(--bg-app)' }}>
      {!embedded && <header style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
        <button onClick={() => navigate('/')} title="SLF" style={{ width: 30, height: 30, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer' }}>
          <img className="app-logo" src="/logo.png" alt="SLF" style={{ width: 28, height: 28, objectFit: 'contain' }} />
        </button>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{t('mod_bookkeeping')}</div>
      </header>}
      {!client ? <ClientPicker onPick={pick} /> : (
        <div style={{ flex: 1, overflow: 'auto', padding: '16px 18px', minHeight: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
            {/* Back goes one step: out of a month to the year, out of the year to the client list. */}
            <button onClick={() => (month ? selectMonth(0) : pick(null))} style={{ ...ddl, fontWeight: 600 }}>
              <i className="ti ti-arrow-left" />{month ? ` ${year}` : ''}
            </button>
            <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--text-primary)' }}>{client.company_name || client.client_name}</div>
            {/* The way through to this client's statutory books. A link rather than a tab: they are
                a separate module behind a separate permission, so somebody without it should land
                on a closed door, not on an empty tab inside this one. */}
            <a href={`/m/accounting_books?client=${encodeURIComponent(client.id)}`} target="_blank" rel="noreferrer"
               title={t('pa_bk_books_link_title')}
               style={{ ...ddl, display: 'inline-flex', alignItems: 'center', gap: 5, textDecoration: 'none', fontWeight: 700 }}>
              <i className="ti ti-book-2" aria-hidden /> {t('pa_bk_books_link')} <i className="ti ti-external-link" style={{ fontSize: 12 }} aria-hidden />
            </a>
            {month > 0 && <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-secondary)' }}>{t(MONTHS[month - 1])} {year}</div>}
            {month > 0 && filed && (
              <span title={t('bk_m_filed')} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 800, color: '#92400E', background: '#FFFBEB', border: '1px solid #F1C232', padding: '2px 8px', borderRadius: 999 }}>
                <i className="ti ti-lock" style={{ fontSize: 12 }} aria-hidden />{t('bk_m_filed')}
              </span>
            )}
            <div style={{ flex: 1 }} />
            <>
              <button onClick={refresh} disabled={refreshing} title={t('txf_refresh')} style={{ ...ddl, background: refreshing ? 'var(--bg-active)' : 'var(--bg-surface)' }}>
                <i className="ti ti-refresh" style={{ display: 'inline-block', animation: refreshing ? 'spin 0.7s linear infinite' : undefined }} /> {t('txf_refresh')}</button>
              <select value={year} onChange={e => selectYear(Number(e.target.value))} style={ddl}>{years.map(y => <option key={y} value={y}>{y}</option>)}</select>
              <select value={month} onChange={e => selectMonth(Number(e.target.value))} style={ddl}>
                <option value={0}>{t('bk_all_months')}</option>
                {MONTHS.map((m, i) => <option key={i} value={i + 1}>{t(m)} {year}</option>)}
              </select>
            </>
          </div>
          {err && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', marginBottom: 12, borderRadius: 8, background: '#FCEBEB', border: '1px solid #F3B9B9', color: '#B42318', fontSize: 12.5 }}>
              <i className="ti ti-alert-triangle" /><span style={{ flex: 1 }}>{err}</span>
              <button onClick={() => setErr('')} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#B42318' }}><i className="ti ti-x" /></button>
            </div>
          )}
          {!month ? (
            ov === null ? <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" /></div> : (<>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                <div style={{ position: 'relative', flex: 1, minWidth: 220, maxWidth: 560 }}>
                  <i className="ti ti-search" style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: 'var(--text-tertiary)' }} aria-hidden />
                  <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('bk_search_year', { year })}
                    style={{ width: '100%', padding: '9px 30px 9px 31px', fontSize: 13, borderRadius: 9, border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box' }} />
                  {!!q && <button onClick={() => setQ('')} style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-tertiary)' }}><i className="ti ti-x" style={{ fontSize: 14 }} /></button>}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                  {hits ? t('bk_search_hits', { n: hits.total, year }) : t('bk_year_line', { lines: yearTotals.lines, done: yearTotals.done, n: yearTotals.n })}
                </div>
              </div>
              {hits ? (
                hits.rows.length === 0 ? <div style={{ padding: 26, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>{t('bk_search_none', { year })}</div> : (
                  <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', background: 'var(--bg-surface)' }}>
                    {hits.rows.map(r => (
                      <div key={r.id} onClick={() => goToHit(r)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                        onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-active)')} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                        <span style={{ width: 62, flexShrink: 0, fontSize: 11, fontWeight: 800, color: 'var(--accent)' }}>{t(MONTHS[(Number(r.month) || 1) - 1])} {year}</span>
                        <span style={{ width: 9, height: 9, flexShrink: 0, borderRadius: 2, background: r.section === 'output' ? '#2B6CB0' : '#1D9E75' }} title={t(r.section === 'output' ? 'tx_output' : 'tx_input')} aria-hidden />
                        <span style={{ width: 88, flexShrink: 0, fontSize: 12, color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>{r.line_date || '—'}</span>
                        <span style={{ width: 120, flexShrink: 0, fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.doc_no || '—'}</span>
                        <span style={{ flex: 1, minWidth: 0, fontSize: 12.5 }}>
                          <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.party_name || '—'}</span>
                          {r.hit_item && <span style={{ display: 'block', fontSize: 11, color: 'var(--text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}><i className="ti ti-list-details" style={{ fontSize: 11, marginRight: 4 }} aria-hidden />{r.hit_item}</span>}
                        </span>
                        <span style={{ fontSize: 12.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{money(r.total)}</span>
                      </div>
                    ))}
                    {hits.total > hits.rows.length && (
                      <div style={{ padding: '8px 12px', fontSize: 11.5, color: 'var(--text-tertiary)' }}>{t('tx_more_results', { n: hits.total - hits.rows.length })}</div>
                    )}
                  </div>
                )
              ) : <YearGrid months={ov.months} year={year} onPick={selectMonth} />}
            </>)
          ) : (<>
            {/* Filed is stated, not enforced (owner 2026-09-25: 不要锁). The invoice figures are
                already frozen — the regenerate route refuses on a filed submission — but the item
                detail feeds no filing total, no VAT figure and no export, so locking it would only
                block the catch-up typing that happens after a return has gone out. */}
            {filed && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', marginBottom: 12, borderRadius: 8, background: '#FFFBEB', border: '1px solid #FDE68A', color: '#92400E', fontSize: 12.5 }}>
                <i className="ti ti-lock" aria-hidden /><span>{t('bk_filed_note')}</span>
              </div>
            )}
            {/* The exceptions, as a strip you clear. 131 invoices firm-wide reconcile against
                nothing, and before this the only way to meet one was to scroll the month it is in. */}
            {(books.output || books.input) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
                {CHIPS.filter(c => c.key === 'all' || c.n > 0 || c.key === flag).map(c => {
                  const on = flag === c.key;
                  return (
                    <button key={c.key} onClick={() => setFlag(c.key)}
                      style={{ padding: '5px 12px', fontSize: 12.5, fontWeight: 600, borderRadius: 20, cursor: 'pointer', whiteSpace: 'nowrap',
                        border: `1px solid ${on ? 'var(--accent)' : 'var(--border)'}`, background: on ? 'var(--accent)' : 'transparent',
                        color: on ? '#fff' : (c.tone || 'var(--text-secondary)') }}>
                      {c.label} {c.n}
                    </button>
                  );
                })}
              </div>
            )}
            {flag !== 'all' && shownRows === 0 && (books.output || books.input) && (
              <div style={{ padding: 26, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>{t('bk_f_none')}</div>
            )}
            {(['output', 'input'] as const).filter(sec => flag === 'all' || !books[sec] || books[sec]!.rows.length > 0).map(sec => (
              <BookTable key={sec} section={sec} book={books[sec]} clientId={client.id}
                openId={openId} onOpen={setOpenId} onImage={setLightbox}
                onEditing={v => { editingRef.current = v; }} onCounts={onCounts} />
            ))}
          </>)}
        </div>
      )}
      {lightbox && <Lightbox images={lightbox} onClose={() => setLightbox(null)} />}
    </div>
  );
}
